import { useQuery } from "@tanstack/react-query";
import { Trip, TripComponent, DayDestination } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { useEffect } from "react";
import { differenceInCalendarDays } from "date-fns";

export type TripPaymentStatus = 'no_invoices' | 'unpaid' | 'partially_paid' | 'fully_paid';

export interface TripInvoiceSummary {
  count: number;
  paymentStatus: TripPaymentStatus;
  departDate: string | null;
  returnDate: string | null;
  travelers: string[];
}

// Helper function to format locations consistently
function formatLocation(location: string | null): string {
  if (!location) return '';
  
  // Extract only the essential parts (city and country/state)
  let formattedLocation: string = '';
  
  // Split by commas
  const locationParts = location.split(',').map(part => part.trim());
  
  if (locationParts.length >= 2) {
    // Take first part as city
    const cityName = locationParts[0];
    
    // Take last part as country/region, or second part if it looks like a country code
    let countryOrRegion = '';
    
    if (locationParts[locationParts.length - 1].trim().length <= 3) {
      // If last part is likely a postal code, take the second to last part
      countryOrRegion = locationParts[locationParts.length - 2];
    } else {
      // Otherwise take the last part
      countryOrRegion = locationParts[locationParts.length - 1];
    }
    
    // Basic cleanup to remove postal codes and other non-country elements
    countryOrRegion = countryOrRegion.replace(/\d+/g, '').trim();
    
    formattedLocation = `${cityName}, ${countryOrRegion}`;
  } else {
    // If only one part, use it as-is
    formattedLocation = locationParts[0];
  }
  
  return formattedLocation;
}

// Extended TripComponent type to include runtime properties used for display
interface ExtendedTripComponent extends TripComponent {
  _isContinuation?: boolean;  // Flag to mark components spanning multiple days
  displayDayIndex?: number;   // Adjusted day index for display
}

export function useTrip(tripId: number) {
  const { toast } = useToast();
  
  // Fetch trip data
  const { 
    data: trip,
    isLoading: isTripLoading,
    error: tripError
  } = useQuery<Trip>({
    queryKey: [`/api/trips/${tripId}`],
    queryFn: async () => {
      const response = await fetch(`/api/trips/${tripId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Failed to fetch trip:', response.status, response.statusText);
        throw new Error(`Failed to fetch trip: ${response.status}`);
      }
      
      const tripData = await response.json();
      return tripData;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 5000, // Consider data stale after 5 seconds
  });

  // Fetch trip components
  const {
    data: components = [],
    isLoading: isComponentsLoading,
    error: componentsError
  } = useQuery<TripComponent[]>({
    queryKey: [`/api/trips/${tripId}/components`],
    queryFn: async () => {
      const response = await fetch(`/api/trips/${tripId}/components`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch trip components: ${response.status}`);
      }
      
      return response.json();
    },
    enabled: !!tripId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 5000, // Consider data stale after 5 seconds
  });
  
  // Fetch day destinations
  const {
    data: dayDestinations = [],
    isLoading: isDayDestinationsLoading,
    error: dayDestinationsError
  } = useQuery<DayDestination[]>({
    queryKey: [`/api/trips/${tripId}/day-destinations`],
    queryFn: async () => {
      const response = await fetch(`/api/trips/${tripId}/day-destinations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch day destinations: ${response.status}`);
      }
      
      return response.json();
    },
    enabled: !!tripId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 5000,
  });

  // Fetch invoice summary for trip (travelers, status, computed dates)
  const {
    data: invoiceSummary,
    isLoading: isInvoiceSummaryLoading,
  } = useQuery<TripInvoiceSummary>({
    queryKey: [`/api/trips/${tripId}/invoices/summary`],
    queryFn: async () => {
      const [summaryRes, travelersRes] = await Promise.all([
        fetch(`/api/trips/${tripId}/invoices/summary`, {
          credentials: 'include',
        }),
        fetch(`/api/trips/${tripId}/invoices/travelers`, {
          credentials: 'include',
        }),
      ]);

      let summary: TripInvoiceSummary = { count: 0, paymentStatus: 'no_invoices', departDate: null, returnDate: null, travelers: [] };
      if (summaryRes.ok) {
        const raw = await summaryRes.json();
        summary = {
          count: raw.invoiceCount ?? raw.count ?? 0,
          paymentStatus: raw.paymentStatus ?? 'no_invoices',
          departDate: raw.departDate ?? null,
          returnDate: raw.returnDate ?? null,
          travelers: raw.travelers ?? [],
        };
      }
      if (travelersRes.ok) {
        const travelers = await travelersRes.json();
        if (Array.isArray(travelers) && travelers.length > 0) {
          summary.travelers = travelers;
        }
      }
      return summary;
    },
    enabled: !!tripId,
    staleTime: 10000,
  });

  // Handle errors with useEffect to prevent re-render loops
  useEffect(() => {
    if (tripError) {
      toast({
        title: "Error",
        description: "Failed to load trip details",
        variant: "destructive",
      });
    }
  }, [tripError, toast]);

  useEffect(() => {
    if (componentsError) {
      toast({
        title: "Error",
        description: "Failed to load trip components",
        variant: "destructive",
      });
    }
  }, [componentsError, toast]);
  
  useEffect(() => {
    if (dayDestinationsError) {
      toast({
        title: "Error",
        description: "Failed to load day destinations",
        variant: "destructive",
      });
    }
  }, [dayDestinationsError, toast]);

  // Calculate date range of the trip with timezone correction
  // Properly handle UTC dates from database and convert to local dates without timezone shifts
  const hasTripDates = !!(trip?.startDate && trip?.endDate);
  const dateRange: [Date, Date] = hasTripDates
    ? [
        new Date(
          new Date(trip!.startDate!).getUTCFullYear(),
          new Date(trip!.startDate!).getUTCMonth(),
          new Date(trip!.startDate!).getUTCDate()
        ),
        new Date(
          new Date(trip!.endDate!).getUTCFullYear(),
          new Date(trip!.endDate!).getUTCMonth(),
          new Date(trip!.endDate!).getUTCDate()
        )
      ]
    : [new Date(), new Date()];

  // When trip has real dates, compute from the date range.
  // When dates are TBD, derive from the highest component dayIndex so all days render.
  const dayCount = !trip
    ? 0
    : hasTripDates
      ? differenceInCalendarDays(dateRange[1], dateRange[0]) + 1
      : Math.max(1, ...components.map(c => (c.dayIndex ?? 0) + 1));
    
  // Group components by day, handling multi-day components
  const groupedByDay: Record<number, ExtendedTripComponent[]> = {};
  const tripStartDate = dateRange[0];
  
  components.forEach(component => {
    // When trip dates are TBD, keep the stored dayIndex as-is — recalculating
    // relative to today would silently reposition every component to the wrong day.
    let dayIndex = component.dayIndex;
    
    if (hasTripDates && component.startDate) {
      const componentDate = new Date(
        new Date(component.startDate).getUTCFullYear(),
        new Date(component.startDate).getUTCMonth(),
        new Date(component.startDate).getUTCDate()
      );
      
      const calculatedDayIndex = differenceInCalendarDays(componentDate, tripStartDate);
      
      if (component.type === 'flight' && component.dayIndex === -1) {
        dayIndex = Math.max(0, calculatedDayIndex);
      } else if (calculatedDayIndex >= 0) {
        dayIndex = calculatedDayIndex;
      }
    }
    
    if (!groupedByDay[dayIndex]) {
      groupedByDay[dayIndex] = [];
    }
    
    // Create a copy with the (potentially) recalculated dayIndex
    const displayComponent = {
      ...component,
      displayDayIndex: dayIndex, // Add the adjusted day index for display purposes
    };
    
    groupedByDay[dayIndex].push(displayComponent);
    
    // If the component has an endDate and it spans multiple days,
    // add it to each day between the start and end dates
    if (component.endDate && component.type === 'hotel') {
      const startDayIndex = dayIndex; // Use the recalculated day index
      
      // Create a local date from the component's UTC endDate 
      const endDate = new Date(
        new Date(component.endDate).getUTCFullYear(),
        new Date(component.endDate).getUTCMonth(),
        new Date(component.endDate).getUTCDate()
      );
      
      const endDayIndex = differenceInCalendarDays(endDate, tripStartDate);
      
      // If it's a multi-day component, add it to each day in between
      if (endDayIndex > startDayIndex) {
        for (let i = startDayIndex + 1; i <= endDayIndex; i++) {
          if (!groupedByDay[i]) {
            groupedByDay[i] = [];
          }
          
          // Create a clone of the component with a special flag to indicate it's a continuation
          const clonedComponent = {
            ...component,
            displayDayIndex: i, // Use the current day in the loop
            _isContinuation: true // Add a flag to mark this as a continuation
          };
          
          groupedByDay[i].push(clonedComponent);
        }
      }
    }
  });

  // Sort components within each day by startTime
  Object.keys(groupedByDay).forEach(day => {
    const dayIndex = parseInt(day);
    groupedByDay[dayIndex].sort((a: ExtendedTripComponent, b: ExtendedTripComponent) => {
      // Always put continuations after primary components
      if (a._isContinuation && !b._isContinuation) return 1;
      if (!a._isContinuation && b._isContinuation) return -1;
      
      // If startTime is not defined, put it at the end
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      
      return a.startTime.localeCompare(b.startTime);
    });
  });

  // Extract unique locations for each day, to be displayed in headers
  const dayLocations: Record<number, string[]> = {};
  
  // First, populate from day destinations if available
  dayDestinations.forEach(destination => {
    const dayIndex = destination.dayIndex;
    
    if (!dayLocations[dayIndex]) {
      dayLocations[dayIndex] = [];
    }
    
    // Format destination to be consistent with component location formatting
    const formattedLocation = formatLocation(destination.destination);
    if (formattedLocation && !dayLocations[dayIndex].includes(formattedLocation)) {
      dayLocations[dayIndex].push(formattedLocation);
    }
  });
  
  // If no day destinations are available or as a fallback, use component locations
  if (Object.keys(dayLocations).length === 0 || 
      Object.values(dayLocations).every(locations => locations.length === 0)) {
    for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
      const dayComponents = groupedByDay[dayIndex] || [];
      const locations = new Set<string>();
      
      dayComponents.forEach(component => {
        if (component.location) {
          // Format the location to be consistent
          const formattedLocation = formatLocation(component.location);
          if (formattedLocation) {
            locations.add(formattedLocation);
          }
        }
      });
      
      dayLocations[dayIndex] = Array.from(locations);
    }
  }
  
  return {
    trip,
    components,
    dayDestinations,
    groupedByDay,
    dayLocations,
    dateRange,
    hasTripDates,
    dayCount,
    invoiceSummary: invoiceSummary ?? null,
    isLoading: isTripLoading || isComponentsLoading || isDayDestinationsLoading
  };
}